import { GIFT_CATEGORIES, OFFICIAL_CAUSES } from "@/constants/productTaxonomy";
import type { Product } from "@/interfaces/product";
import type { GiftContext, GiftLinkUnavailableReason } from "@/interfaces/redemption";
import { htmlToParagraphs } from "@/utils/html-text-helper";

// Non-breaking space so "a choice of gifts..." never wraps mid-phrase.
const NBSP = " ";

/** Status the API answers with for a real (non-preview) link that was already redeemed. */
const GIFT_ALREADY_REDEEMED_STATUS = 410;

/** Status the API answers with for an unknown, archived or expired link — all shown as expired. */
const GIFT_LINK_EXPIRED_STATUS = 404;

/** React Query's own default retry cap for a failed query. */
const DEFAULT_QUERY_RETRY_COUNT = 3;

const GIFT_LINK_DISABLED_STATUS = 409;

/** Structural read of an axios-shaped error's HTTP status (plain `{response:{status}}` or a real `AxiosError`). */
function responseStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null | undefined)?.response?.status;
}

/** 410 → already redeemed; 404 (unknown/archived/expired token) → expired; anything else (network, 5xx) → "failed". */
export function redemptionLinkError(err: unknown): GiftLinkUnavailableReason | "failed" {
  const status = responseStatus(err);
  if (status === GIFT_ALREADY_REDEEMED_STATUS) return "already-redeemed";
  if (status === GIFT_LINK_EXPIRED_STATUS) return "expired";
  if (status === GIFT_LINK_DISABLED_STATUS) return "disabled";
  return "failed";
}

/** Never retry a 4xx (already-redeemed/invalid is never transient); otherwise React Query's own default of 3 attempts. */
export function shouldRetryGiftContext(failureCount: number, err: unknown): boolean {
  const status = responseStatus(err);
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < DEFAULT_QUERY_RETRY_COUNT;
}

/**
 * Recipient headline for the unboxing splash — mirrors the prototype's
 * `landingHeadline` (GfgUnboxing, the design source of truth):
 * "{name} from {company} sent you a choice of gifts…".
 *
 * Joins only non-empty name parts (a missing last name must not leave a double
 * space) and NBSP-joins the tail so the title box wraps identically.
 */
export function buildRecipientHeadline(sender: GiftContext["sender"]): string {
  const first = (sender.firstName ?? "").trim();
  const last = (sender.lastName ?? "").trim();
  const company = (sender.companyName ?? "").trim();
  const name = [first, last].filter(Boolean).join(" ");
  const hasCompany = !!company && company.toLowerCase() !== first.toLowerCase();
  const tail = `a${NBSP}choice${NBSP}of${NBSP}gifts...`;
  if (name && hasCompany) return `${name} from ${company} sent you ${tail}`;
  if (name) return `${name} sent you ${tail}`;
  return `You've been sent ${tail}`;
}

/**
 * Short sender greeting for the thank-you note ask (RFW-9), mirroring the prototype's
 * `senderGreeting`: "{First} at {Company}" when both exist, else "{First} {Last}",
 * falling back to "the sender" when name tokens are missing.
 */
export function senderGreeting(sender: GiftContext["sender"]): string {
  const first = (sender.firstName ?? "").trim();
  const last = (sender.lastName ?? "").trim();
  const company = (sender.companyName ?? "").trim();
  const hasCompany = !!company && company.toLowerCase() !== first.toLowerCase();
  if (first && hasCompany) return `${first} at ${company}`;
  if (first && last) return `${first} ${last}`;
  return "the sender";
}

/**
 * Coarse type bucket a product's category maps to, for the feed's type tabs (Tangible /
 * Charity / Experiences). The real product `category` (product_type) is "Donation" (→ charity)
 * or "Experience" (→ experiences) — the digital gifts, matching isDigitalProduct — everything
 * else is physical (Tangible). Legacy prototype strings ("Charity Impact"/"Experiences") are
 * kept as aliases. TODO(taxonomy): replace with a real product `type` field when the endpoint
 * carries one.
 */
export function giftTypeFromCategory(category: string): "physical" | "charity" | "experiences" {
  if (category === "Donation" || category === "Charity Impact") return "charity";
  if (category === "Experience" || category === "Experiences") return "experiences";
  return "physical";
}

/**
 * Whether a gift appears on a given country's feed. Country-locking is PHYSICAL-only: a physical
 * gift shows only when its `shippableCountries` explicitly lists the country. An EMPTY list means
 * "no Shopify shipping data yet" (an international product Shopify hasn't populated) → hidden from
 * every country until re-sync, NOT treated as ship-anywhere. Charity/experience gifts are
 * country-agnostic and always show. (Domestic-collection physical empties are normalized to ["US"]
 * server-side, so a physical empty reaching here is international.) The server twin lives in the API
 * `utils/product-helper.ts`; keep the two in lockstep.
 */
export function shipsToCountry(
  product: Pick<Product, "category" | "shippableCountries">,
  country: string,
): boolean {
  if (giftTypeFromCategory(product.category) !== "physical") return true;
  return !!product.shippableCountries?.includes(country);
}

/**
 * Classify a chosen gift for the recipient-details branch (RFW-7): physical
 * (tangible) gifts ship to an address; experiences + charity gifts collect name +
 * email only (charity "does good directly", no shipment). Checks the product's
 * display category first, then its raw category. Mirrors the prototype's `giftKind`.
 */
export function giftKind(p: Product): "physical" | "charity" | "experiences" {
  const categories = p.displayCategory ?? [];
  if (
    categories.includes("Charity Gifts") ||
    p.category === "Donation" ||
    p.category === "Charity Impact"
  )
    return "charity";
  if (
    categories.includes("Experiences") ||
    p.category === "Experience" ||
    p.category === "Experiences"
  )
    return "experiences";
  return "physical";
}

/** The gift category a non-physical product type implies; each value must be a GIFT_CATEGORIES member. */
const TYPE_IMPLIED_CATEGORY: Record<
  Exclude<ReturnType<typeof giftTypeFromCategory>, "physical">,
  (typeof GIFT_CATEGORIES)[number]
> = {
  charity: "Charity Gifts",
  experiences: "Experiences",
};

/**
 * A product's effective gift categories: its tagged `displayCategory` plus the category its product
 * type implies. Charity and experience products are mostly untagged in Shopify, so the type
 * supplies "Charity Gifts" / "Experiences"; physical products add nothing.
 */
function effectiveGiftCategories(product: Product): readonly string[] {
  const tagged = product.displayCategory ?? [];
  const kind = giftTypeFromCategory(product.category);
  if (kind === "physical") return tagged;
  const implied = TYPE_IMPLIED_CATEGORY[kind];
  return tagged.includes(implied) ? tagged : [...tagged, implied];
}

/**
 * The Category filter's options: the GFG gift categories at least one of these products carries
 * (tagged, or implied by its product type), in GFG's order. Only the defined categories can appear
 * — a stray value on a product (a legacy native category left by a pre-taxonomy sync) is never offered.
 */
export function availableGiftCategories(products: readonly Product[]): string[] {
  const present = new Set(products.flatMap(effectiveGiftCategories));
  return GIFT_CATEGORIES.filter((category) => present.has(category));
}

/**
 * The Cause filter's options: the official causes at least one of these products supports, as its
 * primary OR a secondary cause. Secondary causes outside the official 6 are not offered.
 */
export function availableCauses(products: readonly Product[]): string[] {
  const present = new Set(products.flatMap((p) => p.causes ?? []));
  return OFFICIAL_CAUSES.filter((cause) => present.has(cause));
}

/** The product carries at least one of the picked categories, tagged or implied by its type. */
export function hasAnyGiftCategory(product: Product, categories: readonly string[]): boolean {
  const own = effectiveGiftCategories(product);
  return categories.some((category) => own.includes(category));
}

/** The product supports at least one of the picked causes, in any position (primary or secondary). */
export function supportsAnyCause(product: Product, causes: readonly string[]): boolean {
  return causes.some((cause) => (product.causes ?? []).includes(cause));
}

/**
 * Whether a Category / Cause filter is worth showing: it earns its place only when it offers a
 * real choice (≥2 options). With one option the sole value is already implied by the active type
 * tab (e.g. "Charity Gifts" on the Charity tab); with none there's nothing to pick — either way
 * the control is a dead end. Driving visibility off the option count (not a hardcoded per-tab
 * list) means it keeps working as new tabs are added.
 */
export function filterOffersChoice(options: readonly string[]): boolean {
  return options.length > 1;
}

/* ── Closing impact reveal (RFW-10) — the "you did good" final screen ── */

// The sample feed occasionally concatenates two image URLs into one impactImage
// string; take the first. (Ported from the prototype's firstImg.)
function firstImpactImg(src?: string): string {
  if (!src) return "";
  const parts = String(src).split("https://").filter(Boolean);
  return parts.length ? `https://${parts[0]}` : "";
}

// Impact tags are Title Case ("Educate Children in Honduras"); the headline
// lowercases them for "You helped …", so re-capitalize the proper nouns / acronyms
// that appear across the feed's tag set (else it reads "…in honduras"). From the prototype.
const IMPACT_PROPER_TERMS = [
  "Honduras",
  "Afghan",
  "Ethiopian",
  "Indigenous",
  "Arctic",
  "New Orleans",
  "LGBTQ",
];
function properizeImpact(phrase: string): string {
  let out = phrase;
  for (const term of IMPACT_PROPER_TERMS) {
    out = out.replace(new RegExp(term, "gi"), term);
  }
  return out;
}

// One sentence of context from the (HTML) impact description — the closing screen
// stays spare, so lead with a single plain sentence, not the full multi-paragraph story.
function firstImpactSentence(html?: string): string {
  const text = htmlToParagraphs(html).join(" ");
  if (!text) return "";
  const match = text.match(/^.*?[.!?](\s|$)/);
  return (match ? match[0] : text).trim();
}

/**
 * Copy + image for the closing impact reveal (RFW-10), mirroring the prototype's
 * RedemptionMessage impact stage. The headline is personal and concrete: the
 * product's `impactTag` is a short imperative ("Plant Trees"), so it reads naturally
 * after "You helped …" with the recipient's first name appended (a leading "Help" is
 * dropped so it doesn't stutter "Help Cancer Patients" → "You helped cancer patients").
 * Falls back to a warm generic line when there's no tag.
 */
export function buildImpactReveal(
  product?: Product,
  recipientFirstName?: string,
): { circleSrc: string; headline: string; lead: string } {
  const first = (recipientFirstName ?? "").trim();
  const suffix = first ? `, ${first}` : "";
  const impactTag = (product?.impactTag ?? "").trim();
  const headline = impactTag
    ? `You helped ${properizeImpact(impactTag.toLowerCase().replace(/^help\s+/, ""))}${suffix}.`
    : `Your choice made a difference${suffix}.`;
  return {
    circleSrc: firstImpactImg(product?.impactImage) || product?.imageUrl || "",
    headline,
    lead: firstImpactSentence(product?.impactDescription),
  };
}

/** Whether a product passes the current feed filters (type tab + category + cause + favorites).
 *  Category and Cause are MULTI-select (OR within each): an empty list means "any", otherwise a
 *  product passes if it matches at least one of the picks. */
export function matchesGiftFilters(
  product: Product,
  filters: {
    activeType: string;
    activeCats: string[];
    activeCauses: string[];
    activeCountry: string;
    favOnly: boolean;
    favorites: Set<string>;
  },
): boolean {
  const { activeType, activeCats, activeCauses, activeCountry, favOnly, favorites } = filters;
  const typeOk = activeType === "all" || giftTypeFromCategory(product.category) === activeType;
  // Any position: a product with 2 categories / several causes matches a pick of either.
  const catOk = activeCats.length === 0 || hasAnyGiftCategory(product, activeCats);
  const causeOk = activeCauses.length === 0 || supportsAnyCause(product, activeCauses);
  const favOk = !favOnly || favorites.has(product.id);
  // Country applies to physical (shipped) gifts only — charity/experience are email-delivered and
  // country-agnostic. A physical gift with an empty shippableCountries (no Shopify data) is hidden.
  const countryOk = shipsToCountry(product, activeCountry);
  return typeOk && catOk && causeOk && favOk && countryOk;
}
